import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';

export interface BookmarkButtonProps {
  saved: boolean;
  onSaveChange: (next: boolean) => void;
  /** Joins the accessible name, so a grid of icon saves isn't a dozen buttons called "Bookmark". */
  itemTitle: string;
  variant?: 'button' | 'icon';
  className?: string;
}

/**
 * BookmarkButton — the one save control across the portal, replacing the legacy "Bookmark"
 * (articles) and "Add to Recipe Box" / "In My Recipe Box" (recipes) labels.
 *
 * Presentational and fully controlled: it renders `saved` and reports the intended next value.
 * Confirmation toasts, optimistic cache patching and failure rollback all belong to the calling
 * toggle hook, which knows when the server actually agreed.
 */
export function BookmarkButton({ saved, onSaveChange, itemTitle, variant = 'button', className }: BookmarkButtonProps) {
  const { t } = useTranslation('shared');
  const label = saved ? t('bookmarks.bookmarked') : t('bookmarks.bookmark');

  // ContentCard wraps its whole surface in a link overlay, so a save must never navigate.
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onSaveChange(!saved);
  };

  const glyph = <Icon name="heart" weight={saved ? 'solid' : 'regular'} size={variant === 'icon' ? 'md' : 'sm'} />;

  if (variant === 'icon') {
    return (
      <Button
        type="button"
        onClick={handleClick}
        aria-pressed={saved}
        data-slot="bookmark-button"
        data-saved={saved ? '' : undefined}
        variant="ghost"
        size="icon"
        className={cn(
          'size-11! border border-border-control-soft bg-card! text-foreground shadow-md! backdrop-blur',
          saved && 'border-primary! text-primary! hover:bg-primary/10!',
          className,
        )}
      >
        {glyph}
        <span className="sr-only">
          {label} {itemTitle}
        </span>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      onClick={handleClick}
      aria-pressed={saved}
      data-slot="bookmark-button"
      data-saved={saved ? '' : undefined}
      variant={saved ? 'secondary' : 'outline'}
      className={cn('min-h-11 gap-2', className)}
    >
      {glyph}
      {label}
      <span className="sr-only"> {itemTitle}</span>
    </Button>
  );
}
